import Autocomplete from '@mui/material/Autocomplete';
import Paper from '@mui/material/Paper';
import PropTypes from 'prop-types';
import React, {useCallback, useMemo, useState} from 'react';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import isEqual from 'lodash/isEqual.js';
import throttle from 'lodash/throttle';
import {InputAdornment} from '@mui/material';
import {useTranslation} from 'react-i18next';

import SearchInputOption from './search-input-option.jsx';
import {
  SOURCE_TYPES,
  getSuggestions,
  isGoogleMapsUrl,
  latLonFromString,
  suggestFromGoogleMapsUrl,
  suggestLatLon,
  suggestMGRS,
  testMGRS,
} from './util.js';
import {TOUR_STAGES} from '@explorer/constants';
import {extentToPolygon} from '@explorer/util/geom';
import {geocode} from '@explorer/util/geocoder.js';
import {notify} from '@explorer/actions/notifications.js';

const SearchInput = ({
  center,
  onClearSearch,
  onSelect,
  inputValue = '',
  onInputChange,
  suggestionsPromises,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const {t} = useTranslation();

  const searchSuggestions = useCallback(
    query => {
      if (query.length < 3) {
        return [];
      }
      return getSuggestions(center, query).then(response => response.features);
    },
    [center]
  );

  const selectResult = async (_, result) => {
    if (result) {
      if (result.source === SOURCE_TYPES.GEOCODER) {
        geocode(center, result.query)
          .then(({features}) => {
            return features[0];
          })
          .then(feature => {
            if (!feature.bbox) {
              // eslint-disable-next-line no-console
              console.error('Geocoder: result did not include bbox');
              return;
            }
            const bboxPoly = extentToPolygon({
              left: feature.bbox[0],
              bottom: feature.bbox[1],
              right: feature.bbox[2],
              top: feature.bbox[3],
            });
            return {feature, geometry: bboxPoly};
          })
          .then(({geometry}) => {
            if (onSelect) {
              onSelect({
                ...result,
                geometry: geometry,
                showCenterMarker: result.suggestion.__type === 'Address',
              });
            }
          });
      } else {
        if (onSelect) {
          onSelect(result);
        }
      }
    }
  };

  const search = useMemo(
    () =>
      throttle((query, callback) => {
        if (latLonFromString(query)) {
          callback(suggestLatLon(query));
        } else if (isGoogleMapsUrl(query)) {
          callback(suggestFromGoogleMapsUrl(query));
        } else if (testMGRS(query) !== -1) {
          callback(suggestMGRS(query));
        } else {
          let requests = [searchSuggestions(query)];

          if (suggestionsPromises) {
            requests = suggestionsPromises
              .map(suggestionFunc => suggestionFunc(query))
              .concat(requests);
          }
          Promise.all(requests)
            .then(allResults => {
              const results = [].concat.apply([], allResults);
              callback(results);
            })
            .catch(() => {
              setLoading(false);
              notify({
                message: t('searchInput.errorSearch'),
                severity: 'error',
              });
            });
        }
      }, 1000),
    [searchSuggestions, suggestionsPromises, t]
  );

  const handleInputChange = (event, newValue, reason) => {
    onInputChange(newValue);

    if (reason === 'clear') {
      setOptions([]);
      onClearSearch();
    } else if (reason === 'input') {
      if (newValue) {
        setLoading(true);
        search(newValue, results => {
          if (results) {
            setOptions(results);
          }
          setLoading(false);
        });
      }
    }
  };

  return (
    <Paper elevation={2} data-tour={TOUR_STAGES.SEARCH_INPUT}>
      <Autocomplete
        freeSolo
        fullWidth
        autoHighlight
        noOptionsText={t('searchInput.noResults')}
        loading={loading}
        isOptionEqualToValue={(option, value) => {
          return isEqual(option, value);
        }}
        getOptionLabel={option => option.label || option.title}
        filterOptions={x => x}
        options={options}
        autoComplete
        includeInputInList
        inputValue={inputValue}
        onChange={selectResult}
        onInputChange={handleInputChange}
        renderOption={({id, ...props}, option) => (
          <SearchInputOption {...props} key={id} option={option} />
        )}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            margin="none"
            placeholder={t('searchInput.prompt')}
            inputProps={{
              ...params.inputProps,
              'data-qe': 'search-input',
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment:
                params.InputProps.endAdornment &&
                React.cloneElement(params.InputProps.endAdornment, {
                  'data-qe': `clear-search-button-${
                    inputValue ? 'visible' : 'hidden'
                  }`,
                }),
            }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
          />
        )}
        sx={{
          '& .MuiAutocomplete-clearIndicatorDirty, & .MuiAutocomplete-clearIndicator':
            {
              visibility: 'visible',
            },
        }}
      />
    </Paper>
  );
};

SearchInput.propTypes = {
  center: PropTypes.array,
  onClearSearch: PropTypes.func,
  onSelect: PropTypes.func,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
  suggestionsPromises: PropTypes.array,
};

export default SearchInput;
